import { useParams, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  useGetApiChannelConnections,
} from "@/lib/api/generated/channels/channels";
import {
  useGetApiChannelDeployments,
  usePostApiChannelDeployments,
} from "@/lib/api/generated/channels/channels";
import { useGetApiAgents } from "@/lib/api/generated/agents/agents";
import { toastMutationError } from "@/lib/utils/formatting";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import type { ControlPlaneAgentRecord } from "@/lib/types/control-plane-agents";
import type {
  ChannelConnectionRecord,
  ChannelDeploymentRecord,
} from "../../../../../packages/shared-types/src/index.js";

const editDeploymentSchema = z.object({
  allowDms: z.boolean(),
  allowGroups: z.boolean(),
  requireMention: z.boolean(),
  status: z.enum(["active", "paused"]),
});

type EditDeploymentForm = z.infer<typeof editDeploymentSchema>;

export function EditDeploymentPage() {
  const { deploymentId } = useParams<{ deploymentId: string }>();
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const mutation = usePostApiChannelDeployments();
  const { data: deployments, isLoading } = useGetApiChannelDeployments<ChannelDeploymentRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  const { data: agents } = useGetApiAgents<ControlPlaneAgentRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  const { data: connections } = useGetApiChannelConnections<ChannelConnectionRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  useDocumentTitle("Edit Deployment");

  const deployment = deployments?.find((d) => d.id === deploymentId);
  const agentName = agents?.find((a) => a.agentId === deployment?.agentId)?.name ?? deployment?.agentId;
  const connName = connections?.find((c) => c.id === deployment?.channelConnectionId)?.displayName ?? deployment?.channelConnectionId;

  const { register, handleSubmit, reset, formState: { errors, isDirty, isSubmitting } } = useForm<EditDeploymentForm>({
    resolver: zodResolver(editDeploymentSchema),
  });

  useEffect(() => {
    if (deployment) {
      reset({
        allowDms: deployment.allowDms,
        allowGroups: deployment.allowGroups,
        requireMention: deployment.requireMention,
        status: deployment.status,
      });
    }
  }, [deployment, reset]);

  const onSubmit = async (data: EditDeploymentForm) => {
    if (!deployment) {return;}
    try {
      await mutation.mutateAsync({
        data: {
          id: deployment.id,
          agentId: deployment.agentId,
          channelConnectionId: deployment.channelConnectionId,
          allowDms: data.allowDms,
          allowGroups: data.allowGroups,
          requireMention: data.requireMention,
          status: data.status,
        },
      });
      toast.success("Deployment updated");
      navigate("/workspace/deployments");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to update deployment"));
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-32"><p className="text-xs" style={{ color: "var(--muted-foreground)" }}>Loading...</p></div>;
  }

  if (!deployment) {
    return <div className="flex items-center justify-center h-32"><p className="text-xs" style={{ color: "var(--muted-foreground)" }}>Deployment not found.</p></div>;
  }

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="text-lg font-semibold mb-6" style={{ color: "var(--text-strong)" }}>
        Edit Deployment
      </h1>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            {/* Read-only info */}
            <div className="grid grid-cols-2 gap-4 text-xs pb-2 border-b border-border">
              <div>
                <span style={{ color: "var(--muted-foreground)" }}>Agent</span>
                <p className="font-medium" style={{ color: "var(--text-strong)" }}>{agentName}</p>
              </div>
              <div>
                <span style={{ color: "var(--muted-foreground)" }}>Connection</span>
                <p className="font-medium" style={{ color: "var(--text-strong)" }}>{connName}</p>
              </div>
              <div>
                <span style={{ color: "var(--muted-foreground)" }}>ID</span>
                <p className="font-mono" style={{ color: "var(--muted-foreground)" }}>{deployment.id}</p>
              </div>
            </div>

            <FormField label="Status">
              <select {...register("status")} className="form-input">
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <CheckboxField label="Allow DMs" {...register("allowDms")} />
              <CheckboxField label="Allow Groups" {...register("allowGroups")} />
              <CheckboxField label="Require Mention" {...register("requireMention")} />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting || !isDirty}>
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate("/workspace/deployments")}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>{label}</label>
      {children}
      {error && <span className="text-xs" style={{ color: "var(--danger)" }}>{error}</span>}
    </div>
  );
}

const CheckboxField = ({ label, ...props }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) => (
  <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--text-strong)" }}>
    <input type="checkbox" {...props} className="rounded border-border" />
    {label}
  </label>
);
